/**
 * WHY AN EMAIL DID NOT ARRIVE, SAID OUT LOUD.
 *
 *   "error sending recovery email in the admin portal"
 *   "The 'Send the confirmation' button doesn't do what it's supposed to do -
 *    it doesn't send the email, error: 'Error sending the change email.'"
 *
 * Both of those are the SAME failure wearing two labels, and neither is a bug
 * in this repository. Supabase Auth raises "Error sending X email" when its
 * SMTP transport refuses or times out; the wording changes with which template
 * was being sent, so it looks like two problems and is one.
 *
 * WHAT THIS FILE IS FOR
 *
 * The portal used to relay the raw string. "Error sending the change email" is
 * true and useless: it tells the person nothing about whether to retry, wait,
 * or go and change a setting. Every branch below turns one of these into the
 * next actual step, and names which setting when the setting is the cause.
 *
 * THE TWO CAUSES, IN THE ORDER THEY ACTUALLY HAPPEN
 *
 * 1. CUSTOM SMTP IS OFF. Supabase's built-in sender is a courtesy, not a mail
 *    service: it is rate limited to a couple of messages an hour AND it will
 *    only deliver to addresses belonging to members of the Supabase project.
 *    That is the trap here -- a password reset to a colleague's Orchid address
 *    is refused outright, while the founder's own address may go through, so
 *    the feature looks like it works until somebody else tries it.
 *
 * 2. CUSTOM SMTP IS ON AND THE SENDER IS NOT AUTHORISED. Brevo (and every
 *    other relay) rejects a message whose From address is not a verified
 *    sender on the account, even when the DOMAIN is authenticated. Domain
 *    authentication and sender authorisation are two separate switches, and
 *    passing the first does not pass the second.
 *
 * Neither is visible from application code -- the API returns the same 500
 * either way -- so this names both rather than guessing between them.
 */

/** Supabase's own wording, across the templates that can raise it. */
const SEND_FAILURE = /error sending (the )?(recovery|confirmation|magic link|invite|change|email|signup)/i;

/**
 * The built-in sender's limit. Distinct from a send failure: the message was
 * refused for being too soon, and waiting genuinely fixes it.
 */
const RATE_LIMITED = /rate limit|too many requests|over_email_send_rate_limit/i;

/** True when this error means "mail did not go out", whatever the template. */
export const isDeliveryFailure = (error) => {
  const message = String(error?.message || error || '');
  return SEND_FAILURE.test(message) || RATE_LIMITED.test(message);
};

/**
 * One sentence a person can act on, and the setting to go and look at.
 *
 * Returns null when the error is not about delivery, so a caller can fall
 * through to its own handling rather than mislabelling an unrelated failure as
 * a mail problem.
 */
export function deliveryProblem(error) {
  const message = String(error?.message || error || '');
  if (!message) return null;

  if (RATE_LIMITED.test(message)) {
    return 'Too many emails have been sent from this project in the last hour, so this one '
         + 'was refused. Wait an hour and try again — or, better, finish configuring custom '
         + 'SMTP, which removes the limit entirely.';
  }

  if (SEND_FAILURE.test(message)) {
    return 'Orchid accepted the request but the email could not be sent, so nothing was '
         + 'delivered. This is a mail configuration problem on the Supabase project, not a '
         + 'problem with this account: either custom SMTP is switched off (the built-in '
         + 'sender only delivers to Supabase project members), or the sending address is not '
         + 'an authorised sender on the SMTP provider. Authenticating the domain is a '
         + 'separate step from authorising the sender, and both are needed.';
  }

  return null;
}

/**
 * The wrapper every email-sending call in the portal goes through.
 *
 * Takes whatever Supabase returned and produces either null (it went) or a
 * sentence worth showing. Callers that need to distinguish "not a mail problem"
 * from "no problem" should test isDeliveryFailure themselves.
 */
export const readableEmailError = (error, fallback = 'That email could not be sent.') => {
  if (!error) return null;
  return deliveryProblem(error) || error.message || fallback;
};
